import type { SourceAuthorityType } from '../../../core/data-profiles/sourceAuthorityTypes';
import type { DataSource } from '../models/DataSource';
import type { DiagramDataSourceConnection } from '../models/DiagramDataSourceConnection';
import type { FolderSourceAuthority } from '../models/FolderSourceAuthority';
import type { FolderSourceBinding } from '../models/FolderSourceBinding';

export interface ReviewedDiagramConnection extends DiagramDataSourceConnection {
  dataSource: DataSource | null;
}

/**
 * Resolves a legacy diagram connection only when it is backed by the reviewed
 * folder authority for the requested coherent source type. Array order and a
 * sole connection are never treated as authority.
 */
export function resolveReviewedPrimaryConnection<TConnection extends ReviewedDiagramConnection>(
  connections: readonly TConnection[],
  authorities: readonly FolderSourceAuthority[],
  bindings: readonly FolderSourceBinding[],
  sourceType: SourceAuthorityType,
): TConnection | null {
  const authority = authorities.find((candidate) =>
    candidate.sourceType === sourceType
    && candidate.state === 'active'
    && candidate.authorityKind === 'binding'
    && Boolean(candidate.bindingId));
  if (!authority?.bindingId) return null;

  const binding = bindings.find((candidate) =>
    candidate.id === authority.bindingId
    && candidate.status === 'active'
    && !candidate.archivedAt);
  if (!binding?.dataSourceId) return null;

  return connections.find((connection) =>
    connection.dataSourceId === binding.dataSourceId
    && Boolean(connection.dataSource)) ?? null;
}
